import React, { useState, useEffect, useMemo } from 'react';
import { SpeedDialProps } from './types';
import TabNavigation from './TabNavigation';
import SpeedDialCard from './SpeedDialCard';

export const SpeedDial: React.FC<SpeedDialProps> = ({
    data,
    defaultColumns = 5,
    defaultActiveTab,
    onTabChange,
    editable = true,
    onEnableEdit,
    defaultTarget = 'self',
}) => {
    const tabs = Object.keys(data);
    if (!defaultActiveTab) defaultActiveTab = tabs[defaultActiveTab ?? 0];
    const [activeTab, setActiveTab] = useState<string>(defaultActiveTab || '');

    // Extract color mapping from data options
    const tabColors = useMemo(() => {
        return tabs.reduce(
            (acc, tab) => {
                if (data[tab].options?.color) {
                    acc[tab] = data[tab].options!.color!;
                }
                return acc;
            },
            {} as Record<string, string>,
        );
    }, [data, tabs]);

    // Safety check if data changes and activeTab is no longer valid
    useEffect(() => {
        if (!tabs.includes(activeTab) && tabs.length > 0) {
            setActiveTab(tabs[0]);
        }
    }, [data, tabs, activeTab]);

    const currentCategory = data[activeTab];
    const currentItems = currentCategory?.items || [];

    // Determine effective columns: specific tab options > global default
    const specificColumns = currentCategory?.options?.cols;
    const effectiveColumns = specificColumns || defaultColumns;

    const onTabChangeHandler = (tab: string) => {
        setActiveTab(tab);
        if (onTabChange) onTabChange(tab);
    };
    return (
        <div className="w-full">
            {/* Navigation Tabs - Only show if more than 1 category exists */}
            {tabs.length > 1 && (
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={onTabChangeHandler}
                    colors={tabColors}
                    editable={editable}
                    onEnableEdit={onEnableEdit}
                />
            )}

            {/* Dynamic Responsive Grid Style */}
            <style>{`
        .speed-dial-grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(${Math.min(effectiveColumns, 2)}, minmax(0, 1fr));
        }
        @media (min-width: 640px) {
          .speed-dial-grid {
            gap: 1.5rem;
            grid-template-columns: repeat(${Math.min(effectiveColumns, 3)}, minmax(0, 1fr));
          }
        }
        @media (min-width: 1024px) {
          .speed-dial-grid {
            grid-template-columns: repeat(${Math.min(effectiveColumns, 4)}, minmax(0, 1fr));
          }
        }
        @media (min-width: 1280px) {
          .speed-dial-grid {
            grid-template-columns: repeat(${effectiveColumns}, minmax(0, 1fr));
          }
        }
      `}</style>

            {/* Grid Content */}
            {currentItems.length > 0 ? (
                <div className="speed-dial-grid">
                    {currentItems.map((item, index) => (
                        <div key={`${item.url}-${index}`} className="w-full min-w-0">
                            <SpeedDialCard item={item} defaultTarget={defaultTarget} />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-slate-500">
                    <svg
                        className="w-16 h-16 mb-4 text-gray-300 dark:text-slate-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                    </svg>
                    <p className="text-lg">This section is empty.</p>
                </div>
            )}
        </div>
    );
};

export default SpeedDial;
